import type { ReactNode } from "react";
import { t } from "@chuzi/shared/config";
import type {
  RealmId,
  SceneListItem,
  StoryListItem,
  TextLine,
} from "@chuzi/shared/types";
import { DefaultSceneSlate } from "./default-scene-slate";
import { TextPreviewPanel, formatMs } from "./generator-timeline";
import { resolveTransmissionStageKind } from "../lib/scene-viewer-data";

interface SceneTransmissionStageProps {
  scene: SceneListItem;
  story: StoryListItem;
  realm: RealmId;
  textLines: TextLine[];
  playheadMs: number;
  isPlaying: boolean;
  fontFamily: string;
  textColor: string;
  backgroundColor: string;
  fontSize?: number;
  primaryMediaUrl: string | null;
  mediaUrls: Record<string, string>;
  coverboxUrl: string | null;
  effectiveDurationMs: number;
  overlay?: ReactNode;
  readOnly?: boolean;
  showTransport?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onSeek?: (ms: number) => void;
  onLinePositionChange?: (id: string, x: number, y: number) => void;
  selectedEffectId?: string | null;
  onEffectResize?: (id: string, w: number, h: number) => void;
  onPreviewAreaRef?: (el: HTMLDivElement | null) => void;
  headerLabel?: string;
  onFilmEnded?: () => void;
  slateInteractive?: boolean;
  onDirectorClick?: (directorId: string) => void;
  fullScreen?: boolean;
}

function PreviewTransport({
  realm,
  playheadMs,
  effectiveDurationMs,
  isPlaying,
  showTransport,
  onPlay,
  onPause,
  onSeek,
}: {
  realm: RealmId;
  playheadMs: number;
  effectiveDurationMs: number;
  isPlaying: boolean;
  showTransport: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onSeek?: (ms: number) => void;
}) {
  if (!showTransport || !onSeek || !onPlay || !onPause) return null;
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.9)" : "rgba(126,184,255,0.9)";
  const duration = Math.max(effectiveDurationMs, 1);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
      <button
        type="button"
        onClick={isPlaying ? onPause : onPlay}
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          border: "1px solid rgba(232,240,255,0.2)",
          background: "rgba(232,240,255,0.06)",
          color: "#e8f0ff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
        }}
        title={isPlaying ? t(realm, "editor_preview_pause", "Pause") : t(realm, "editor_preview_play", "Play")}
      >
        {isPlaying ? "⏸" : "▶"}
      </button>
      <input
        type="range"
        min={0}
        max={duration}
        value={playheadMs}
        onChange={(e) => onSeek(Number(e.target.value))}
        style={{ flex: 1, height: 4, accentColor: accent as string }}
      />
      <div style={{ fontSize: 10, color: "rgba(232,240,255,0.5)", fontVariantNumeric: "tabular-nums", minWidth: 50, textAlign: "right" }}>
        {formatMs(playheadMs)} / {formatMs(duration)}
      </div>
    </div>
  );
}

function stageShell(
  realm: RealmId,
  content: ReactNode,
  overlay: ReactNode | undefined,
  transport: ReactNode,
  centered = false,
  fullScreen = false,
) {
  const isWilds = realm === "wilds";
  const frameStyle: React.CSSProperties = fullScreen
    ? {
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }
    : {
        position: "relative",
        overflow: "hidden",
        borderRadius: 10,
        border: `1px solid ${isWilds ? "rgba(120,200,120,0.25)" : "rgba(126,184,255,0.25)"}`,
        boxShadow: "0 4px 32px rgba(0,0,0,0.4)",
        display: "flex",
        flexDirection: "column",
        ...(centered
          ? {
              width: "100%",
              maxHeight: "100%",
              aspectRatio: "16 / 9",
            }
          : {
              flex: 1,
              minHeight: 120,
            }),
      };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, flex: 1, minHeight: 0, height: fullScreen ? "100%" : undefined }}>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: centered && !fullScreen ? "center" : undefined,
          justifyContent: centered && !fullScreen ? "center" : undefined,
          height: fullScreen ? "100%" : undefined,
        }}
      >
        <div style={frameStyle}>
          {content}
          {overlay}
        </div>
      </div>
      {transport}
    </div>
  );
}

export function SceneTransmissionStage({
  scene,
  story,
  realm,
  textLines,
  playheadMs,
  isPlaying,
  fontFamily,
  textColor,
  backgroundColor,
  fontSize,
  primaryMediaUrl,
  mediaUrls,
  coverboxUrl,
  effectiveDurationMs,
  overlay,
  readOnly = false,
  showTransport = true,
  onPlay,
  onPause,
  onSeek,
  onLinePositionChange,
  selectedEffectId,
  onEffectResize,
  onPreviewAreaRef,
  headerLabel,
  onFilmEnded,
  slateInteractive = false,
  onDirectorClick,
  fullScreen = false,
}: SceneTransmissionStageProps) {
  const stageKind = resolveTransmissionStageKind(scene, textLines, primaryMediaUrl);
  const transport = (
    <PreviewTransport
      realm={realm}
      playheadMs={playheadMs}
      effectiveDurationMs={effectiveDurationMs}
      isPlaying={isPlaying}
      showTransport={showTransport}
      onPlay={onPlay}
      onPause={onPause}
      onSeek={onSeek}
    />
  );
  const creatorAvatarUrl =
    typeof story.creator === "object" && story.creator
      ? ("avatar_url" in story.creator && typeof story.creator.avatar_url === "string"
          ? story.creator.avatar_url
          : null)
      : null;

  const centeredStage = readOnly && !fullScreen;

  if (stageKind === "slate") {
    return stageShell(
      realm,
      <DefaultSceneSlate
        variant={scene.is_title ? "title" : "end"}
        storyTitle={story.title}
        directorName={story.creator?.name ?? null}
        directorId={story.creator?.id ?? null}
        directorAvatarUrl={creatorAvatarUrl}
        realm={realm}
        interactive={slateInteractive}
        fill={readOnly}
        backgroundUrl={coverboxUrl ?? null}
        onDirectorClick={onDirectorClick}
      />,
      overlay,
      transport,
      centeredStage,
      fullScreen,
    );
  }

  if (stageKind === "film" && primaryMediaUrl) {
    return stageShell(
      realm,
      <video
        key={primaryMediaUrl}
        src={primaryMediaUrl}
        autoPlay
        playsInline
        style={{ width: "100%", height: "100%", objectFit: fullScreen ? "cover" : "contain", background: "#000" }}
        onEnded={onFilmEnded}
      />,
      overlay,
      transport,
      centeredStage,
      fullScreen,
    );
  }

  if (stageKind === "image" && primaryMediaUrl) {
    return stageShell(
      realm,
      <img
        src={primaryMediaUrl}
        alt={scene.title}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />,
      overlay,
      transport,
      centeredStage,
      fullScreen,
    );
  }

  const textPanel = (
    <TextPreviewPanel
      lines={textLines}
      fontFamily={fontFamily}
      textColor={textColor}
      backgroundColor={backgroundColor}
      fontSize={fontSize}
      realm={realm}
      playheadMs={playheadMs}
      isPlaying={isPlaying}
      onPlay={onPlay ?? (() => {})}
      onPause={onPause ?? (() => {})}
      onSeek={onSeek ?? (() => {})}
      totalDurationMs={effectiveDurationMs}
      onLinePositionChange={onLinePositionChange}
      selectedEffectId={selectedEffectId}
      onEffectResize={onEffectResize}
      overlay={overlay}
      onPreviewAreaRef={onPreviewAreaRef}
      readOnly={readOnly}
      showTransport={showTransport}
      mediaUrls={mediaUrls}
      headerLabel={headerLabel ?? t(realm, "editor_preview_label", "Preview")}
      fullScreen={false}
    />
  );

  if (readOnly) {
    const framedTextStage = stageKind === "composed";
    return stageShell(
      realm,
      textPanel,
      undefined,
      transport,
      framedTextStage || centeredStage,
      framedTextStage ? false : fullScreen,
    );
  }

  return textPanel;
}
